import { useKeycloak } from '@react-keycloak/web';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Stack } from '@mui/material';

import { useMutation } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { SuccessResponse } from '@/api/data-contracts';
import { Generatik } from '@/app/ui/SideMenu/styled';
import LeftImage from '@/assets/Advertising.png';
import { ReactComponent as AccessDeninedImage } from '@/assets/Fingerprintsecurity.svg';
import RightImage from '@/assets/PPCDigitalMarketingCampaignandPayPerClickAdvertising.png';
import { ReactComponent as Logo } from '@/assets/logo.svg';
import { ROUTES } from '@/config/routes';
import { useUsersService } from '@/features/users';
import { useAccessDenied } from '@/shared/lib/AccessDeniedContext/AccessDeniedContext';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { useClearQueryCache } from '@/shared/lib/hooks/useClearQueryCache';
import { resetNavigationHistory } from '@/shared/lib/hooks/useNavigationHistory';
import useTableStore from '@/shared/model/tableStore';

import {
  Container,
  Content,
  CornerImage,
  Description,
  Divider,
  Title,
} from './AccessDenied.styles';

function AccessDenied() {
  const { t } = useTranslation();
  const { requestAccessToModule } = useUsersService();
  const { enqueueSnackbar } = useSnackbar();
  const clearAll = useTableStore(useShallow((state) => state.clearAll));
  const { keycloak } = useKeycloak();
  const { setDenied } = useAccessDenied();
  const clearQueryCache = useClearQueryCache();
  const userType = React.useContext(UserTypeContext);
  const navigate = useNavigate();

  const subdomain = React.useMemo(() => {
    switch (userType) {
      case 'location':
        return 'location';
      case 'service':
        return 'services';
      case 'advertiser':
        return 'advertiser';
      default:
        return 'backoffice';
    }
  }, [userType]);

  const goHome = () => {
    if (window.location.origin.includes(`${subdomain}.`)) {
      navigate(ROUTES.AUTHORIZED.LANDING);
      setDenied(false);
    } else {
      window.location.href = window.location.origin.replace(
        /^(https?:\/\/)?([^./]+)/,
        (_, protocol = '') => {
          return `${protocol}${subdomain}`;
        },
      );
    }
  };

  const { mutateAsync } = useMutation(
    () =>
      requestAccessToModule(
        window.location.host.split('.')[0].toUpperCase() as
          | 'LOCATION'
          | 'ADVERTISER'
          | 'SERVICE_PROVIDER'
          | 'BACKOFFICE',
      ),
    {
      onSuccess: async (response: SuccessResponse) => {
        if (response.success) {
          enqueueSnackbar(t('access_succesfully_requested'), { variant: 'success' });
        } else {
          enqueueSnackbar(t('access_requested_error'), { variant: 'error' });
        }

        goHome();
      },
    },
  );

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '2rem',
          gap: '0.5rem',
          zIndex: Number.MAX_SAFE_INTEGER,
        }}
      >
        <Logo />
        <Generatik isExpanded />
      </Stack>

      <Content>
        <Title>{t('access_denied')}</Title>
        <AccessDeninedImage />

        <Description>{t('error_page.forbidden.description')}</Description>

        <Button
          variant="contained"
          onClick={() => mutateAsync()}
          sx={{
            padding: '0.75rem 1.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.875rem',
            lineHeight: '150%',
          }}
        >
          {t('request_access')}
        </Button>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="center" gap="1rem">
          <Button
            variant="text"
            onClick={() => {
              goHome();
            }}
          >
            {t('return_home')}
          </Button>

          <Button
            variant="text"
            onClick={() => {
              clearAll();
              resetNavigationHistory();
              clearQueryCache();
              keycloak.logout();
            }}
          >
            {t('buttons.logout')}
          </Button>
        </Stack>
      </Content>

      <CornerImage alt="left-image" src={LeftImage} style={{ left: 0 }} />
      <CornerImage alt="right-image" src={RightImage} style={{ right: 0 }} />
    </Container>
  );
}

export default AccessDenied;
